import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { getPublicSurvey } from '@/features/nps/api/use-nps';
import { participantApi, type ParticipantMe } from './participant-api';

/**
 * Cache das abas: trocar de aba não busca de novo nem mostra skeleton — os
 * dados ficam 1 min frescos e revalidam em segundo plano depois disso.
 */

/** O legado não pagina (só `limit`): busca de uma vez e a tela mostra em páginas. */
const HISTORY_LIMIT = 300;
const STALE_MS = 60_000;
export const SURVEY_LANG = 'pt-BR';

export const participantKeys = {
  all: (idPassbook: string) => ['participant', idPassbook] as const,
  history: (idPassbook: string) => ['participant', idPassbook, 'history'] as const,
  coupons: (idPassbook: string) => ['participant', idPassbook, 'coupons'] as const,
  couponEligibility: (idPassbook: string) => ['participant', idPassbook, 'coupons', 'eligibility'] as const,
  mgm: (idPassbook: string) => ['participant', idPassbook, 'mgm'] as const,
  survey: (idPassbook: string, surveyId: string) => ['participant', idPassbook, 'survey', surveyId] as const,
};

const historyQuery = (idPassbook: string) => ({
  queryKey: participantKeys.history(idPassbook),
  queryFn: () => participantApi.history(idPassbook, HISTORY_LIMIT).then((data) => data.logs),
  staleTime: STALE_MS,
});

const couponsQuery = (idPassbook: string) => ({
  queryKey: participantKeys.coupons(idPassbook),
  queryFn: () => participantApi.coupons(idPassbook).then((data) => data.coupons),
  staleTime: STALE_MS,
});

const surveyQuery = (idPassbook: string, surveyId: string) => ({
  queryKey: participantKeys.survey(idPassbook, surveyId),
  queryFn: () => getPublicSurvey(surveyId, SURVEY_LANG),
  staleTime: 5 * STALE_MS,
});

export function useParticipantHistory(idPassbook: string) {
  return useQuery(historyQuery(idPassbook));
}

export function useParticipantCoupons(idPassbook: string) {
  return useQuery(couponsQuery(idPassbook));
}

/** Mostra "Resgatar": elegível e ainda sem cupom. */
export function useCanClaimCoupon(idPassbook: string) {
  return useQuery({
    queryKey: participantKeys.couponEligibility(idPassbook),
    queryFn: () => participantApi.couponEligibility(idPassbook),
    select: (data) => data.eligible && !data.alreadyClaimed,
    staleTime: STALE_MS,
  });
}

export function useClaimCoupon(idPassbook: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => participantApi.claimCoupon(idPassbook),
    onSettled: () => client.invalidateQueries({ queryKey: participantKeys.coupons(idPassbook) }),
  });
}

export function useParticipantMgm(idPassbook: string) {
  return useQuery({
    queryKey: participantKeys.mgm(idPassbook),
    queryFn: () => participantApi.mgm(idPassbook),
    staleTime: STALE_MS,
  });
}

export function useParticipantSurvey(idPassbook: string, surveyId: string) {
  return useQuery({ ...surveyQuery(idPassbook, surveyId), enabled: Boolean(surveyId) });
}

/**
 * Logo depois de entrar, adianta as abas leves para a primeira abertura já
 * vir pronta. O Indique e ganhe fica de fora: a primeira consulta cria o
 * código do participante no MGM, e isso só deve acontecer se ele abrir a aba.
 */
export function prefetchParticipant(client: QueryClient, idPassbook: string, me: ParticipantMe): void {
  void client.prefetchQuery(historyQuery(idPassbook));
  if (me.features.coupons) void client.prefetchQuery(couponsQuery(idPassbook));
  if (me.features.surveyId) void client.prefetchQuery(surveyQuery(idPassbook, me.features.surveyId));
}

/** Saiu (ou a sessão acabou): nada da conta anterior fica em memória. */
export function clearParticipant(client: QueryClient, idPassbook: string): void {
  client.removeQueries({ queryKey: participantKeys.all(idPassbook) });
}
